import React, { useCallback } from 'react';
import querystring from 'query-string';
import {
  HandleLink,
  GNB_REMODELING_EVENT,
  GNB_REMODELING_GOODS,
  GNB_REMODELING_REHOUSE,
  GNB_REMODELING_SECTION,
  GNB_REMODELING_SHOP,
  GNB_REMODELING_START,
  NewDisplayHandleLink,
  NewDisplayHandleAction,
  NewDisplayHandleLinkArgs,
} from '@display-component/shared';
import { useRouter } from 'next/router';
import {
  GNB_HOMEIDEA_MODULE_BY_TEMPLATE_NO,
  GNB_MALL_MODULE_BY_TEMPLATE_NO,
  GNB_REMODELING_MODULE_BY_TEMPLATE_NO,
} from '@remodeling/content';
import { authGateway } from '@/di-container/auth/gateway/client';
import {
  NewTextButton,
  useAlert,
  useConfirm,
  useToast,
} from '@design-system/pc';
import { ALERT_Z_INDEX, CONFIRM_Z_INDEX, TOAST_Z_INDEX } from '@/styles';
import TrackingService from '@/services/tracking';

export type HandleOpenAppArgs = {
  target?: string;
  channel?: string;
  module?: string;
  value?: string;
};

const useDisplayComponent = () => {
  const router = useRouter();

  const handleOldOpenUrl = useCallback(
    (url = '', wiseLogParam?: string) => {
      const targetUrl = TrackingService.combineWiseLogParam(
        url.startsWith('http')
          ? url
          : `${process.env.NEXT_PUBLIC_WEB_HOST}${
              url.startsWith('/') ? '' : '/'
            }${url}`,
        wiseLogParam,
      );

      if (typeof window !== 'undefined') {
        window.location.href = targetUrl;
      } else {
        router.push(targetUrl);
      }
    },
    [router],
  );

  const handleAppLinkGnbRemodeling = useCallback<HandleLink>(
    (appLink) => {
      const gnbModule = appLink?.appLinkVal
        ? GNB_REMODELING_MODULE_BY_TEMPLATE_NO[appLink.appLinkVal]
        : undefined;
      if (gnbModule) {
        switch (gnbModule) {
          case GNB_REMODELING_SECTION:
            handleOldOpenUrl('/main', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_GOODS:
            handleOldOpenUrl('/goods', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_SHOP:
            handleOldOpenUrl('/shop/search', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_START:
            handleOldOpenUrl('/remodeling-start', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_EVENT:
            handleOldOpenUrl('/event', appLink?.wiseLogParam);
            break;
          case GNB_REMODELING_REHOUSE:
            handleOldOpenUrl('/rehouse', appLink?.wiseLogParam);
            break;
          default:
            handleOldOpenUrl(
              `/main?module=${gnbModule}`,
              appLink?.wiseLogParam,
            );
            break;
        }
      } else {
        handleOldOpenUrl(`/`, appLink?.wiseLogParam);
      }
    },
    [handleOldOpenUrl],
  );

  const handleAppLinkCateRemodeling = useCallback<HandleLink>(
    (appLink) => {
      const query = querystring.stringify({
        ctgNo: appLink?.appLinkVal,
      });
      handleOldOpenUrl(`/goods?${query}`, appLink?.wiseLogParam);
    },
    [handleOldOpenUrl],
  );

  const handleAppLinkGnbMall = useCallback<HandleLink>(
    (appLink) => {
      const gnbModule = appLink?.appLinkVal
        ? GNB_MALL_MODULE_BY_TEMPLATE_NO[appLink.appLinkVal]
        : undefined;
      if (gnbModule) {
        switch (gnbModule) {
          default:
            handleOldOpenUrl(
              `${process.env.NEXT_PUBLIC_HANSSEMMALL_WEB_HOST}/mall.html?module=${gnbModule}`,
              appLink?.wiseLogParam,
            );
            break;
        }
      } else {
        handleOldOpenUrl(
          `${process.env.NEXT_PUBLIC_HANSSEMMALL_WEB_HOST}/mall.html`,
          appLink?.wiseLogParam,
        );
      }
    },
    [handleOldOpenUrl],
  );
  const handleAppLinkCateMall = useCallback<HandleLink>(
    (appLink) => {
      if (appLink && appLink.appLinkOption) {
        const categoryClass =
          appLink.appLinkOption.ctgDepth === '2'
            ? 'L'
            : appLink.appLinkOption.ctgDepth === '3'
            ? 'M'
            : 'S';
        const query = querystring.stringify({
          ctgrNo: appLink.appLinkOption.ctgNo,
          categoryall: `${categoryClass}${appLink.appLinkOption.ctgNo}`,
        });

        handleOldOpenUrl(
          `${process.env.NEXT_PUBLIC_HANSSEMMALL_WEB_HOST}/category/goHsm${categoryClass}ctgy.do?${query}`,
          appLink?.wiseLogParam,
        );
      } else {
        handleOldOpenUrl(
          `${process.env.NEXT_PUBLIC_HANSSEMMALL_WEB_HOST}`,
          appLink?.wiseLogParam,
        );
      }
    },
    [handleOldOpenUrl],
  );

  const handleAppLinkGnbHomeIdea = useCallback<HandleLink>(
    (appLink) => {
      const gnbModule = appLink?.appLinkVal
        ? GNB_HOMEIDEA_MODULE_BY_TEMPLATE_NO[appLink.appLinkVal]
        : undefined;
      if (gnbModule) {
        switch (gnbModule) {
          default:
            handleOldOpenUrl(
              `${process.env.NEXT_PUBLIC_HANSSEMMALL_WEB_HOST}/interior.html?module=${gnbModule}`,
              appLink?.wiseLogParam,
            );
            break;
        }
      } else {
        handleOldOpenUrl(
          `${process.env.NEXT_PUBLIC_HANSSEMMALL_WEB_HOST}/interior.html`,
          appLink?.wiseLogParam,
        );
      }
    },
    [handleOldOpenUrl],
  );

  const handleLink = useCallback<HandleLink>(
    (appLink) => {
      if (appLink?.appLinkTarget === 'LINK') {
        if (appLink?.appLinkVal) {
          handleOldOpenUrl(appLink.appLinkVal, appLink?.wiseLogParam);
        }
      }
      if (appLink?.appLinkTarget === 'GNB') {
        switch (appLink?.appLinkChannel) {
          case 'MALL':
            handleAppLinkGnbMall(appLink);
            break;
          case 'HOMEIDEA':
            handleAppLinkGnbHomeIdea(appLink);
            break;
          default:
            handleAppLinkGnbRemodeling(appLink);
            break;
        }
      }
      if (appLink?.appLinkTarget === 'CATE') {
        switch (appLink?.appLinkChannel) {
          case 'MALL':
            handleAppLinkCateMall(appLink);
            break;
          default:
            handleAppLinkCateRemodeling(appLink);
            break;
        }
      }
      if (appLink?.appLinkTarget === 'PLAN') {
        if (appLink?.appLinkVal) {
          handleOldOpenUrl(
            `/event/${appLink.appLinkVal}?eventStatusType=ING`,
            appLink?.wiseLogParam,
          );
        }
      }
    },
    [
      handleAppLinkCateMall,
      handleAppLinkCateRemodeling,
      handleAppLinkGnbHomeIdea,
      handleAppLinkGnbMall,
      handleAppLinkGnbRemodeling,
      handleOldOpenUrl,
    ],
  );

  /**
   * appLink와 defaultUrl을 받아 타겟 url로 랜딩하는 함수
   * @param {NewDisplayHandleLinkArgs} appLink 전시 데이터를 담고 있습니다.
   * @param {string} defaultUrl defaultUrl 을 입력하면 linkUrl 이 비어있을때 지정된 defaultUrl 로 이동합니다. linkUrl 과 defaultUrl 둘다 없다면 '/' 로 이동합니다.
   * @returns void
   */
  const handleOpenUrl = useCallback(
    (appLink: NewDisplayHandleLinkArgs, defaultUrl?: string) => {
      if (typeof window === 'undefined') {
        return;
      }

      const url = appLink.applink?.linkUrl ?? (defaultUrl || '/');

      if (appLink.isNewWindow) {
        window.open(url, '_blank');
      } else {
        window.location.href = url;
      }
    },
    [],
  );

  const handleNewLink = useCallback<NewDisplayHandleLink>(
    (appLink) => {
      console.log('[TEST-DISPLAY - appLink]', appLink);
      if (
        (appLink?.applink && !appLink.applink.appLinkTarget) ||
        appLink?.applink?.appLinkTarget === 'URL'
      ) {
        handleOpenUrl(appLink);
      }

      if (appLink?.applink?.appLinkTarget === 'GNB') {
        switch (appLink?.applink?.appLinkChannel) {
          case 'MAIN':
            handleOpenUrl(appLink);
            break;
          case 'MALL':
            handleOpenUrl(
              appLink,
              `${process.env.NEXT_PUBLIC_STORE_WEB_HOST}/furnishing`,
            );
            break;
          case 'HOMEIDEA':
          case 'REMODELING':
            handleOpenUrl(
              appLink,
              `${process.env.NEXT_PUBLIC_STORE_WEB_HOST}/interior`,
            );
            break;
          default:
            handleOpenUrl(appLink);
            break;
        }
      }
      if (appLink?.applink?.appLinkTarget === 'CATEGORY') {
        handleOpenUrl(appLink);
      }

      if (appLink?.applink?.appLinkTarget === 'BNB') {
        switch (appLink?.applink?.appLinkChannel) {
          case 'MAIN':
            handleOpenUrl(appLink);
            break;
          case 'FURNISHING':
            handleOpenUrl(appLink, '/furnishing');
            break;
          case 'INTERIOR':
            handleOpenUrl(appLink, '/interior');
            break;
          case 'SHOPSEARCH':
            handleOpenUrl(appLink, '/shop/search');
            break;
          case 'MY':
            handleOpenUrl(appLink, '/my');
            break;
          default:
            handleOpenUrl(appLink);
            break;
        }
      }

      if (appLink?.applink?.appLinkTarget === 'PLAN') {
        handleOpenUrl(
          appLink,
          `${process.env.NEXT_PUBLIC_STORE_WEB_HOST}
          ${appLink?.applink?.linkUrl}${
            appLink.applink.appLinkAddQuery
              ? '&' + appLink.applink.appLinkAddQuery
              : ''
          }`,
        );
      }

      if (appLink?.goodsInfo) {
        handleOpenUrl(
          appLink,
          `${process.env.NEXT_PUBLIC_STORE_WEB_HOST}/goods/${appLink.goodsInfo.gdsNo}`,
        );
      }
    },
    [handleOpenUrl],
  );

  const handleLogin = useCallback(async () => {
    await authGateway().loginGateway.exec();
  }, []);

  const { toast } = useToast();

  const { handleOpen: handleAlertOpen } = useAlert();
  const { handleOpen: handleConfirmOpen, handleClose: closeConfirm } =
    useConfirm();

  const handleGoScrap = useCallback(() => {
    router.push(
      `${process.env.NEXT_PUBLIC_HANSSEMMALL_HOST}/mypage/wishList.do`,
    );
  }, [router]);

  const handleAction = useCallback<NewDisplayHandleAction>(
    ({ message, action, type }) => {
      console.log('[action]', { message, action, type });

      if (action === 'scrap') {
        toast.success('찜 목록에 추가하였습니다.', {
          placement: 'bottom',
          variant: 'button',
          actionButton: (
            <NewTextButton
              size="lg"
              isUnderline
              color="color_chip.white"
              onClick={handleGoScrap}
              width={74}
            >
              찜 목록가기
            </NewTextButton>
          ),
          offset: 87,
          zIndex: TOAST_Z_INDEX,
        });
      } else {
        switch (type) {
          case 'toast':
            toast.success(message, {
              alignment: 'center',
              placement: 'bottom',
              variant: 'basic',
              offset: 87,
              zIndex: TOAST_Z_INDEX,
            });
            break;

          case 'alert':
            handleAlertOpen({ content: message, zIndex: ALERT_Z_INDEX });
            break;

          case 'confirm':
            if (action === 'login') {
              console.log('[confirm]');
              handleConfirmOpen({
                cancelText: '취소',
                confirmText: '로그인',
                content: message,
                width: '370px',
                isShowCloseButton: true,
                zIndex: CONFIRM_Z_INDEX,
                handleConfirm: () => {
                  handleLogin();
                },
                handleCloseButton: () => {
                  closeConfirm();
                },
              });
            } else {
              handleConfirmOpen({
                cancelText: '취소',
                confirmText: '로그인',
                content: message,
                width: '370px',
                zIndex: CONFIRM_Z_INDEX,
              });
            }
            break;

          default:
            break;
        }
      }
    },
    [
      closeConfirm,
      handleAlertOpen,
      handleConfirmOpen,
      handleGoScrap,
      handleLogin,
      toast,
    ],
  );

  return {
    handleLink,
    handleNewLink,
    handleAction,
  };
};

export default useDisplayComponent;
